import React from 'react';
import { ConfigProvider } from 'antd';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ProConfigProvider } from '@ant-design/pro-components';
import enUS from 'antd/es/locale/en_US';
import { AuthProvider } from './AuthContext';
import { LayoutProvider } from './LayoutContext';
import { RouteProvider } from './RouteContext';
import { LoadingProvider } from './LoadingContext';
import { routesConfig } from '../routesConfig';

// Create a single QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

export function AppProviders({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        locale={enUS}
        componentSize="middle"
        theme={{
          token: {
            // Set primary color to match menu item background (typically #001529 in Ant Design's default dark theme)
            // colorPrimary: '#233867',
            // Make disabled text darker for better readability
            colorTextDisabled: 'rgba(0, 0, 0, 0.65)',
          },
          components: {
            Button: {
              // fontWeight: 'bold',
              fontSize: 12,
              colorPrimary: '#233867',
              colorPrimaryHover: '#3a4f7d', // Slightly lighter
              colorPrimaryActive: '#324878', // Light, but still shows "pressed" state
              // colorPrimaryHover: '#002140', // Slightly darker for hover state
              // colorPrimaryActive: '#000c17', // Even darker for active state
              colorLink: '#233867',
              colorLinkHover: '#3a4f7d',
              colorLinkActive: '#324878',
            },
            Tabs: {
              colorPrimary: '#233867',
              colorPrimaryHover: '#3a4f7d',
              colorPrimaryActive: '#324878',
            },
            Typography: {
              colorLink: '#233867',
              colorLinkHover: '#3a4f7d',
              colorLinkActive: '#324878',
            },
            Form: {
              itemMarginBottom: 4,
              fontSize: 12,
            },
            Input: {
              fontSize: 12,
            },
            InputNumber: {
              fontSize: 12,
            },
            DatePicker: {
              fontSize: 12,
            },
            Select: {
              fontSize: 12,
            },
            Switch: {
              fontSize: 12,
              colorPrimary: '#233867',
              colorPrimaryHover: '#3a4f7d', // Slightly lighter
              colorPrimaryActive: '#324878', // Light, but still shows "pressed" state
            },
            Checkbox: {
              fontSize: 12,
              colorPrimary: '#233867',
              colorPrimaryHover: '#3a4f7d', // Slightly lighter
              colorPrimaryActive: '#324878', // Light, but still shows "pressed" state
            },
            Card: {
              padding: 12,
            },
          },
        }}
      >
        <ProConfigProvider hashed={false}>
          <BrowserRouter future={{ v7_relativeSplatPath: true }}>
            <AuthProvider>
              <LoadingProvider>
                <LayoutProvider initialRoutesConfig={routesConfig}>
                  <RouteProvider>{children}</RouteProvider>
                </LayoutProvider>
              </LoadingProvider>
            </AuthProvider>
          </BrowserRouter>
        </ProConfigProvider>
      </ConfigProvider>
    </QueryClientProvider>
  );
}
